import includes from 'lodash/includes';
import {hot} from 'react-hot-loader/root';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {renderRoutes} from 'react-router-config';
import {Helmet} from 'react-helmet';
import ErrorBoundary from '@spothero/ui/error-boundary';
import NotificationContainer from '@spothero/ui/notification-container';
import NotificationPropTypes from '@spothero/ui/notification-prop-types';
import {clearNotification} from 'notification/notification-actions';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import {replace} from 'router/router-actions';
import {USER_DATA} from 'user/user-prop-types';
import SharedMeta from 'common/meta/SharedMeta';
import GDPRModal from 'common/gdpr/GDPRModal';
import RouterLoader from 'router/RouterLoader';
import HeaderDesktop from 'header/HeaderDesktop';
import HeaderMobile from 'header/HeaderMobile';
import HeaderCheckout from 'header/HeaderCheckout';
import Footer from 'footer/Footer';
import FooterCheckout from 'footer/FooterCheckout';
import withConfig from 'config/withConfig';

class App extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        location: PropTypes.shape({
            pathname: PropTypes.string,
        }).isRequired,
        pageType: PropTypes.string.isRequired,
        cloudfrontViewerCountryCode: PropTypes.string,
        citySlugs: PropTypes.array,
        collapsedNavData: PropTypes.array.isRequired,
        expandedNavData: PropTypes.array.isRequired,
        footerData: PropTypes.array.isRequired,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        notification: PropTypes.shape(NotificationPropTypes),
        clearNotifications: PropTypes.func.isRequired,
        isMonthly: PropTypes.bool.isRequired,
        isRouteLoading: PropTypes.bool.isRequired,
        isSearchPage: PropTypes.bool.isRequired,
        initialSearchUrl: PropTypes.string,
        serverContext: PropTypes.object,
        route: PropTypes.object.isRequired,
        user: PropTypes.shape(USER_DATA).isRequired,
        replaceUrl: PropTypes.func.isRequired,
    };
    state = {
        showGDPRModal: false,
        userCountryCode: null,
    };

    constructor(props) {
        super(props);

        const {
            location: {pathname},
            isSearchPage,
            pageType,
            initialSearchUrl,
            replaceUrl,
        } = props;
        const isSpotDetailsUrl = includes(pathname, '/spot-details/');

        if ((isSearchPage && !isSpotDetailsUrl) || pageType === 'checkout') {
            replaceUrl(initialSearchUrl);
        }
    }

    componentDidMount() {
        this._checkIfForeignUser();
    }

    _onGDPRModalHidden = () => {
        this.setState({
            showGDPRModal: false,
        });
    };

    _checkIfForeignUser() {
        const {cloudfrontViewerCountryCode} = this.props;
        const isForeignUser =
            cloudfrontViewerCountryCode &&
            cloudfrontViewerCountryCode !== 'US' &&
            cloudfrontViewerCountryCode !== 'CA';

        if (isForeignUser) {
            this.setState({
                showGDPRModal: true,
                userCountryCode: cloudfrontViewerCountryCode,
            });
        }
    }

    render() {
        const {
            isMobile,
            notification,
            clearNotifications,
            pageType,
            citySlugs,
            user: {email},
            destination,
            route: {routes},
            isMonthly,
            isRouteLoading,
            expandedNavData,
            collapsedNavData,
            footerData,
            serverContext,
        } = this.props;
        const {showGDPRModal, userCountryCode} = this.state;
        const isCheckout = pageType === 'checkout';
        const HeaderComponent = isCheckout
            ? HeaderCheckout
            : isMobile
            ? HeaderMobile
            : HeaderDesktop;
        const FooterComponent = isCheckout ? FooterCheckout : Footer;
        const hiddenClassName = isRouteLoading ? 'hidden' : null;

        return (
            <ErrorBoundary>
                <SharedMeta />
                {isMobile ? (
                    <Helmet>
                        <meta httpEquiv="cleartype" content="on" />
                        <meta
                            name="viewport"
                            content="width=device-width, initial-scale=1, minimal-ui"
                        />
                        <meta name="HandheldFriendly" content="True" />
                        <meta name="MobileOptimized" content="320" />
                        <meta name="mobile-web-app-capable" content="yes" />
                        <meta
                            name="apple-mobile-web-app-capable"
                            content="yes"
                        />
                        <meta
                            name="apple-mobile-web-app-status-bar-style"
                            content="black"
                        />
                        <meta
                            name="apple-mobile-web-app-title"
                            content="SpotHero"
                        />
                        <meta name="theme-color" content="#002d5b" />
                    </Helmet>
                ) : (
                    <Helmet>
                        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                        <meta
                            name="viewport"
                            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
                        />
                    </Helmet>
                )}
                <div className="App">
                    <NotificationContainer
                        notification={notification}
                        onQueueEmpty={clearNotifications}
                    />
                    <HeaderComponent
                        className={hiddenClassName}
                        expandedData={expandedNavData}
                        collapsedData={collapsedNavData}
                    />
                    {isRouteLoading ? (
                        <RouterLoader />
                    ) : (
                        renderRoutes(routes, {
                            pageType,
                            configData: {
                                isMobile,
                                citySlugs,
                            },
                            isMonthly,
                            destination,
                            serverContext,
                        })
                    )}
                    <FooterComponent
                        className={hiddenClassName}
                        data={footerData}
                        email={email}
                    />
                    {showGDPRModal && (
                        <GDPRModal
                            onHidden={this._onGDPRModalHidden}
                            countryCode={userCountryCode}
                        />
                    )}
                </div>
            </ErrorBoundary>
        );
    }
}

const mapStateToProps = state => {
    const {
        destination: {data: destinationData},
        notification,
        searchRequest: {
            isRouteLoading,
            isSearchPage,
            pageType,
            monthly: isMonthly,
        },
        user: {data: user},
    } = state;

    return {
        destination: destinationData,
        notification,
        user,
        pageType,
        isMonthly,
        isRouteLoading,
        isSearchPage,
    };
};

const mapDispatchToProps = {
    clearNotifications: clearNotification,
    replaceUrl: replace,
};

const enhance = compose(
    hot,
    withConfig(['citySlugs', 'cloudfrontViewerCountryCode', 'isMobile']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(App);
